/**
 * Google Analytics 4 event helpers — the ONE analytics facade, both platforms.
 *
 * WEB: the base gtag.js + `gtag('config', …)` is loaded in index.html,
 * PRODUCTION ONLY (gated to `empathease.app`, skipped on staging + Capacitor
 * native). `window.gtag` is simply absent off-prod and every helper below
 * no-ops — no env var, no extra gate to maintain.
 *
 * NATIVE (GA phase 2, 2026-08): the same events route to Firebase Analytics
 * (`@capacitor-firebase/analytics` — the messaging plugin's sibling; the
 * Firebase project + both native config files already existed for FCM), which
 * feeds the SAME GA4 property as app data streams. Every call site works on
 * both platforms without knowing which one it is on. The plugin import is
 * dynamic so the web bundle never pays for it.
 *
 * Two tiers, mirroring utils/reddit-pixel.ts on purpose:
 *
 *   - trackGaEvent()      — behavioral/engagement, CONSENT-GATED via
 *                           hasTrackingConsent() (same decision as the Reddit
 *                           pixel's trackUserEvent). Native mirror:
 *                           syncAnalyticsConsent() → setEnabled(consent).
 *   - trackGaConversion() — hard conversions (sign_up / subscribe / purchase),
 *                           NOT gated. No PII in params, analogous to the
 *                           reddit-pixel trackConversion: an ad-optimizer needs
 *                           to actually receive the conversion. Keep emails/ids
 *                           out of params. NEVER content: no message text, no
 *                           needs/feelings values (GA-INSTRUMENTATION.md hard
 *                           policy).
 *
 * Footnote — the games subdomain: games/index.html and the StoryForge comic
 * load the SAME GA4 property through a HOST-GATED snippet with no consent
 * layer of its own (public content site: no accounts, no PII, nothing to
 * disclose). The one consent signal it honors is ours — the in-app StoryForge
 * card (utils/storyforge.ts) appends na=1 when hasTrackingConsent() is false,
 * and the snippet then loads nothing for that session.
 */

import { Capacitor } from '@capacitor/core';
// Type-only: erased at build, so the web bundle still tree-shakes the plugin.
import type { FirebaseAnalyticsPlugin } from '@capacitor-firebase/analytics';
import { hasTrackingConsent } from './reddit-pixel';

type GtagFn = (command: string, eventOrId: string, params?: Record<string, unknown>) => void;

// gtag is injected by the GA4 snippet in index.html (prod only). Declare it on
// Window so the helpers are typed without per-call casts — same pattern as
// reddit-pixel.ts declaring `rdt`.
declare global {
  interface Window {
    gtag?: GtagFn;
  }
}

function getGtag(): GtagFn | null {
  if (typeof window === 'undefined') return null;
  return typeof window.gtag === 'function' ? window.gtag : null;
}

const isNative = (): boolean => Capacitor.isNativePlatform();

/**
 * Dynamic import so web bundles tree-shake the Firebase plugin entirely.
 * Failures are swallowed at every call site by design: the reddit-pixel
 * contract ("tracking-side failures can never surface as app errors")
 * applies to the whole facade.
 */
let firebaseModule: Promise<FirebaseAnalyticsPlugin> | null = null;
function firebase() {
  // Memoized: exactly ONE dynamic import however many trackers fire in the
  // same tick (concurrent dynamic imports of one module also deadlock under
  // vitest's mock registry — the routing litmus caught it).
  firebaseModule ??= import('@capacitor-firebase/analytics').then((m) => m.FirebaseAnalytics);
  return firebaseModule;
}

/** Low-level emit; routes by platform, no-ops wherever analytics is absent. */
function emit(event: string, params?: Record<string, unknown>): void {
  if (isNative()) {
    void firebase()
      .then((fa) => fa.logEvent({ name: event, params: (params ?? {}) as never }))
      .catch(() => {});
    return;
  }
  const gtag = getGtag();
  if (!gtag) return;
  gtag('event', event, params ?? {});
}

/**
 * Behavioral / engagement event — consent-gated.
 * Use for funnel steps, content engagement, product actions.
 */
export function trackGaEvent(event: string, params?: Record<string, unknown>): void {
  if (!hasTrackingConsent()) return;
  emit(event, params);
}

/**
 * Hard conversion event — NOT consent-gated, NO PII in params.
 * Use only for sign_up / subscribe / purchase (the events campaigns optimize
 * toward). Identity correlation stays gated at its own call sites.
 */
export function trackGaConversion(event: string, params?: Record<string, unknown>): void {
  emit(event, params);
}

/**
 * Explicit GA4 page_view with a correct title/path. GA4 Enhanced Measurement
 * captures page_view on SPA history changes using document.title AT THAT MOMENT,
 * which can be stale if the title updates a tick later. Calling this AFTER the
 * route's title is set records the right title. Consent-gated (page_view is
 * behavioral). See providers.tsx RouteTracker.
 *
 * On native the same call also logs `screen_view`: Capacitor is ONE native
 * screen, so Firebase's automatic screen tracking sees nothing without it.
 */
export function trackGaPageView(path: string, title: string): void {
  if (!hasTrackingConsent()) return;
  emit('page_view', { page_path: path, page_title: title });
  if (isNative()) {
    void firebase()
      .then((fa) => fa.setCurrentScreen({ screenName: path, screenClassOverride: title }))
      .catch(() => {});
  }
}

/**
 * Cross-device identity: the SAME user across web + iOS + Android in one GA4
 * property. Call with the user's numeric id on login, null on logout. The id
 * is a pseudonymous key, not PII-in-params; identity linking is exactly what
 * GA4's user_id feature is for.
 */
export function setAnalyticsUser(userId: number | string | null): void {
  const id = userId == null ? null : String(userId);
  if (isNative()) {
    void firebase()
      .then((fa) => fa.setUserId({ userId: id }))
      .catch(() => {});
    return;
  }
  const gtag = getGtag();
  if (!gtag) return;
  gtag('config', 'G-SFH4JMD5TK', { user_id: id, send_page_view: false });
}

/** Non-identifying audience property (e.g. plan tier). Never PII. */
export function setAnalyticsUserProperty(key: string, value: string | null): void {
  if (isNative()) {
    void firebase()
      .then((fa) => fa.setUserProperty({ key, value: value ?? '' }))
      .catch(() => {});
    return;
  }
  const gtag = getGtag();
  if (!gtag) return;
  gtag('set', 'user_properties', { [key]: value });
}

/**
 * Native consent mirror: the web tier-1 gate lives per-call in
 * trackGaEvent, but Firebase ALSO auto-collects (first_open, sessions,
 * screen engagement), and that collection must follow the same consent
 * decision. Call on app start and whenever consent changes. Conversions
 * remain ungated per policy — they fire through logEvent regardless of
 * this switch only on web; on native, disabling collection disables
 * everything, which is the stricter and therefore acceptable reading.
 */
let lastNativeConsent: boolean | null = null;

/** Test seam: module-level consent-transition state survives between tests. */
export function resetAnalyticsConsentTrackingForTests(): void {
  lastNativeConsent = null;
}

export function syncNativeAnalyticsConsent(consent: boolean): void {
  if (!isNative()) return;
  // Consent turning ON records the CURRENT screen (Codex round 12 on #816,
  // consolidating the three initial-route fixes): every path that lands
  // here — fresh-anonymous boot, /me resolving a consented user after the
  // unknown window, settle-anonymous, an opted-out user enabling
  // collection mid-session — needs the screen the user is on, and none of
  // them gets a route transition to record it. Chained AFTER setEnabled
  // resolves: Firebase drops events while collection is disabled.
  const replay = consent && lastNativeConsent !== true;
  lastNativeConsent = consent;
  // This call is ASYNC and runs after native SDK init — on its own it
  // cannot stop the automatic first_open/session_start of the boot window.
  // The native configs ship collection DISABLED by default (AndroidManifest
  // meta-data + Info.plist key; Codex P1 on #818, drift-guarded by
  // native-analytics-boot-default.test.ts), and because setEnabled persists
  // across executions, this transition is what turns a consenting user on —
  // once — and the persisted value governs every later boot.
  void firebase()
    .then(async (fa) => {
      await fa.setEnabled({ enabled: consent });
      if (replay) {
        trackGaPageView(window.location.pathname, document.title);
      }
    })
    .catch(() => {});
}

/**
 * CONSENT GATES IDENTITY (Codex P1 on #816): an opted-out logged-in user
 * gets user_id and plan_tier CLEARED, not set — otherwise
 * gtag('config', user_id) correlates their ungated conversions with the
 * identity they opted out of. Anonymous visitors keep collection on (the
 * same policy the reddit-pixel consent checker encodes).
 */
export function applyAnalyticsIdentity(
  user: {
    id: number | string;
    plan_id?: string | null;
    data_collection_consent?: boolean | null;
  } | null,
  /** The subscription store's live planId — auth.user.plan_id goes stale
   * the moment a purchase lands (Codex round 12: an opted-out user who
   * buys and THEN opts in must not get the login-time tier reapplied). */
  authoritativePlanTier?: string | null,
): void {
  const consent = !user || user.data_collection_consent === true;
  setAnalyticsUser(user && consent ? user.id : null);
  setAnalyticsUserProperty(
    'plan_tier',
    user && consent ? (authoritativePlanTier ?? user.plan_id ?? null) : null,
  );
  syncNativeAnalyticsConsent(consent);
}
